<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { useRouter } from 'vue-router';
import { useCrm } from '../composables/useCrm';
import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const { t } = useI18n();
const router = useRouter();
const { accountId } = useAccount();
const { activePipeline, moveConversationToStage } = useCrm();

const getConversationById = useMapGetter('getConversationById');
const currentConversation = computed(() =>
  getConversationById.value(props.conversationId)
);

const currentLabels = computed(() => currentConversation.value?.labels || []);

const currentStage = computed(() => {
  if (!activePipeline.value || !activePipeline.value.stages) return null;
  const stages = activePipeline.value.stages;

  if (currentConversation.value?.status === 'resolved') {
    const resolveStage = stages.find(s => s.action === 'resolve');
    if (resolveStage) return resolveStage;
  }

  const matched = stages.find(
    s =>
      (s.label && currentLabels.value.includes(s.label)) ||
      (s.id && currentLabels.value.includes(s.id))
  );
  if (matched) return matched;

  if (currentConversation.value?.meta?.assignee && stages[1]) {
    return stages[1];
  }
  return stages[0] || null;
});

const handleStageChange = async event => {
  const rawValue = event.target.value;
  if (!rawValue) return;
  const targetStageId = !isNaN(Number(rawValue)) ? Number(rawValue) : rawValue;
  await moveConversationToStage(props.conversationId, targetStageId);
};

const goToCrmKanban = () => {
  router.push({
    name: 'crm_view',
    params: { accountId: accountId.value },
  });
};
</script>

<template>
  <div
    v-if="
      activePipeline &&
      activePipeline.stages &&
      activePipeline.stages.length > 0
    "
    class="mb-3"
  >
    <AccordionItem :title="t('CRM.STAGE_SELECTOR.TITLE')" is-open compact>
      <div
        class="p-3 flex flex-col gap-2 bg-slate-50 dark:bg-slate-800/50 rounded-lg border border-slate-200 dark:border-slate-700/60 text-xs"
      >
        <div class="flex items-center justify-between">
          <span class="text-slate-500 font-medium">{{
            t('CRM.STAGE_SELECTOR.FUNNEL_LABEL')
          }}</span>
          <span class="font-semibold text-slate-800 dark:text-slate-200">{{
            activePipeline?.name || t('CRM.STAGE_SELECTOR.DEFAULT_FUNNEL')
          }}</span>
        </div>

        <div class="flex flex-col gap-1 mt-1">
          <label class="text-slate-500 font-medium">{{
            t('CRM.STAGE_SELECTOR.STAGE_LABEL')
          }}</label>
          <select
            :value="currentStage?.id"
            class="w-full bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded px-2.5 py-1.5 text-xs text-slate-800 dark:text-slate-200 font-medium focus:ring-2 focus:ring-woot-500 focus:outline-none cursor-pointer"
            @change="handleStageChange"
          >
            <option
              v-for="stage in activePipeline?.stages"
              :key="stage.id"
              :value="stage.id"
            >
              {{ stage.name }}
            </option>
          </select>
        </div>

        <button
          type="button"
          class="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-woot-500 hover:bg-woot-600 text-[#1c1c1c] rounded font-semibold transition-colors text-xs shadow-sm cursor-pointer"
          @click="goToCrmKanban"
        >
          <span>{{ t('CRM.STAGE_SELECTOR.VIEW_IN_CRM') }}</span>
        </button>
      </div>
    </AccordionItem>
  </div>
</template>
